import { c as _c } from "react/compiler-runtime";
import React, { FunctionComponent, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { HeaderConfig, ManageEntitiesContentLayout } from 'record-it-components/pageContentLayout/ManageEntitiesContentLayout/ManageEntitiesContentLayout';
import { prepareProps } from 'record-it-components/utils/propUtils';
import { PropsWithoutChildren } from 'record-it-components/utils/utilityTypes';
import { EntityName } from 'record-it-time-common/EntityName';
import { assertNotNullOrUndefined } from 'record-it-utils/utils/assert/assertNotNullOrUndefined';
import { ConstructionSite } from '../../../classes/entities/ConstructionSite/constructionSiteTypes';
import { UserGroup } from '../../../classes/entities/UserGroup/userGroupTypes';
import { iconConfigurations } from '../../../classes/icon/iconConfigurations';
import { RoutePaths } from '../../../classes/RoutePaths/RoutePaths';
import { archivedFilter } from '../../../filters/archivedFilter/archivedFilter';
import { constructionSiteTextFilter } from '../../../filters/constructionSiteTextFilter/constructionSiteTextFilter';
import { useEntities } from '../../../hooks/entity/useEntities/useEntities';
import { useUserGroupCreateEntityButtonConfig } from '../../../hooks/entity/useUserGroupCreateEntityButtonConfig';
import { useConfigureBreadcrumbsWithBuilder } from '../../../hooks/misc/useConfigureBreadcrumbsWithBuilder';
import { useCurrentUser } from '../../../hooks/useCurrentUser';
import { ConstructionSiteList } from '../../constructionSite/ConstructionSiteList/ConstructionSiteList';
export type ConstructionSiteAdminPageProps = PropsWithoutChildren<Record<never, never>>;
export const ConstructionSiteAdminPage: FunctionComponent<ConstructionSiteAdminPageProps> = React.memo(props => {
  const $ = _c(19);
  prepareProps({
    props,
    ignoreChildren: true
  });
  const navigate = useNavigate();
  const {
    t
  } = useTranslation();
  const {
    getRequiredCurrentUserPermissions
  } = useCurrentUser();
  useConfigureBreadcrumbsWithBuilder(_temp);
  const constructionSites = useEntities(EntityName.ConstructionSite, "getAll");
  let t0;
  if ($[0] !== navigate) {
    t0 = userGroup => {
      assertNotNullOrUndefined(userGroup, "can't create a ConstructionSite without a usergroup");
      navigate(RoutePaths.adminConstructionSitesCreateConstructionSite(userGroup.id));
    };
    $[0] = navigate;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const createConstructionSite = t0;
  let t1;
  if ($[2] !== t) {
    t1 = t("components.page.ConstructionSiteAdminPage.title");
    $[2] = t;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== t1) {
    t2 = {
      iconConfig: iconConfigurations.prefixed.page.ConstructionSiteAdminPage.titleIcon,
      title: t1
    };
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const headerConfig = t2;
  const renderEntities = _temp2;
  let t3;
  if ($[6] !== t) {
    t3 = t("components.page.ConstructionSiteAdminPage.create");
    $[6] = t;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== getRequiredCurrentUserPermissions) {
    t4 = getRequiredCurrentUserPermissions().getEditableUserGroupsForConstructionSites();
    $[8] = getRequiredCurrentUserPermissions;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== createConstructionSite || $[11] !== t3 || $[12] !== t4) {
    t5 = {
      text: t3,
      userGroups: t4,
      onCreateEntity: createConstructionSite
    };
    $[10] = createConstructionSite;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const createEntityButtonConfig = useUserGroupCreateEntityButtonConfig(t5);
  let t6;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = [constructionSiteTextFilter, archivedFilter];
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const filters = t6;
  let t7;
  if ($[15] !== constructionSites || $[16] !== createEntityButtonConfig || $[17] !== headerConfig) {
    t7 = <ManageEntitiesContentLayout headerConfig={headerConfig} createEntityButtonConfig={createEntityButtonConfig} entities={constructionSites} renderEntities={renderEntities} filters={filters} data-testid="ConstructionSiteAdminPage.ManageEntitiesContentLayout" />;
    $[15] = constructionSites;
    $[16] = createEntityButtonConfig;
    $[17] = headerConfig;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  return t7;
});
function _temp(builder) {
  return builder.addHomePage().addConstructionSiteAdminPage();
}
function _temp2(currentPageConstructionSites) {
  return <ConstructionSiteList constructionSites={currentPageConstructionSites} data-testid="ConstructionSiteAdminPage.ConstructionSiteList" />;
}